import { useEffect, useState } from 'react'
import { createModal } from '../ModalFactory'
import { AuthContext, useAuth } from 'context/AuthContext'
import { ModalWrapper } from '../ModalWrapper'
import {
    TabStrip,
    TabStripSelectEventArguments,
    TabStripTab,
} from '@progress/kendo-react-layout'
import GeneralForm from './GeneralForm'
import Translation from 'components/Translation'
import IntegrationsForm from './IntegrationsForm'
import { useAppContext } from 'context/AppContext'
import AlertPrompt from 'components/AlertPrompt'
import { disconnectStripe } from 'services/integrations/stripe'
import LocationSpaces from './LocationSpaces'
import { useTenantContext } from 'context/TenantContext'

const ModalProvider = createModal(
    AuthContext,
    'LocationModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Create</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-chevron-left " />
        </span>
    )
)

export const LocationModalOpener = ModalProvider.Opener
export const LocationModalCloser = ModalProvider.Closer

function LocationModal() {
    const { LocationModal: self, attributes, setAttributes } = useAuth()
    const { translations, loadComponentTranslations } = useAppContext()
    const [selected, setSelected] = useState<number>(0)
    const [is_loading, isLoading] = useState<boolean>()
    const { getIntegrations } = useTenantContext()
    const handleSelect = (e: TabStripSelectEventArguments) => {
        setSelected(e.selected)
    }
    const activated_integration =
        attributes.activated_integration as Record<string, string>
    const state_params: Record<string, string> = {}

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        params.get('state') &&
            params
                .get('state')
                ?.split('&')
                .map((pair: string) => {
                    const [key, val] = pair.split('=')
                    state_params[key] = val
                })
    }, [])

    useEffect(() => {
        if (state_params.integration_id) {
            setSelected(2)
        }
    }, [state_params.integration_id])



    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const state_params: Record<string, string> = {}

        params.get('state') &&
            params
                .get('state')
                ?.split('&')
                .map((pair: string) => {
                    const [key, val] = pair.split('=')
                    state_params[key] = val
                })
        if (state_params.integration_id) {
            setSelected(2)
        }
    }, [])

    useEffect(() => {
        if (!translations.INTEGRATIONS_TRANSLATION_ID) {
            loadComponentTranslations('INTEGRATIONS_TRANSLATION_ID')
        }
    }, [translations])

    return (
        <ModalProvider.Visible>
            <ModalWrapper>
                <div className="bg-white shadow-3xl w-full overflow-x-hidden overflow-y-auto sm:rounded-md mx-auto my-4 relative border-2 border-gray-100 flex flex-col">
                    <div className="flex px-4 text-gray-500 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <div>
                            <ul className="ml-6 text-xs flex gap-1 breadcrumbs text-gray-400">
                                <li>Dashboard</li>
                                <li>Location</li>
                            </ul>
                            <span
                                className="inline-block self-center text-lg text-primary-dark"
                                onClick={() => {
                                    setSelected(0)
                                    self.setAttributes({})
                                }}
                            >
                                <LocationModalCloser className="self-center" />
                                <span className="circular">
                                    {/* New Location */}
                                    {self.attributes?.id ? (
                                        self.attributes?.name
                                    ) : (
                                        <Translation content_key="new_location" />
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                    <TabStrip
                        selected={selected}
                        onSelect={handleSelect}
                        className="z-20"
                        animation={false}
                    >
                        <TabStripTab
                            title={<Translation content_key="general" />}
                        >
                            <div className="p-5 w-full">
                                <GeneralForm />
                            </div>
                        </TabStripTab>
                        <TabStripTab
                            title={<Translation content_key="spaces" />}
                        >
                            <div className="p-5 w-full">
                                <LocationSpaces />
                            </div>
                        </TabStripTab>
                        <TabStripTab
                            title={
                                <Translation content_key="integrations" />
                            }
                        >
                            <IntegrationsForm />
                        </TabStripTab>
                    </TabStrip>
                    <AlertPrompt
                        title="are_you_sure_disconnect"
                        show={!!attributes.to_disconnect}
                        className="absolute top-0 flex flex-col items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm w-full h-full z-20"
                        handlebars={
                            attributes.connected_account_id
                                ? {
                                    integration:
                                        attributes.name as unknown as string,
                                }
                                : {}
                        }
                        icon={
                            attributes?.logo ? (
                                <div
                                    className="rounded-full mx-auto h-16 w-16 bg-contain relative"
                                    style={{
                                        backgroundImage: `url(${attributes?.logo})`,
                                    }}
                                >
                                    <i className="feather-x bg-red-200/80 text-red-700 rounded-full py-2 px-2 block absolute -top-1 -right-1" />
                                </div>
                            ) : (
                                <div className="rounded-full mx-auto h-16 w-16 bg-slate-600 relative">
                                    <i className="feather-x bg-red-200 text-red-700 rounded-full py-2 px-2 block absolute top-0 right-0" />
                                </div>
                            )
                        }
                        actions={[
                            <button
                                key="back_button"
                                type="button"
                                className="rounded-lg h-10 px-6 border-slate-800/30 border"
                                onClick={() => {
                                    delete attributes.to_disconnect
                                    isLoading(false)
                                    setAttributes({
                                        ...attributes,
                                    })
                                }}
                                disabled={is_loading}
                            >
                                <Translation content_key="cancel" />
                            </button>,
                            <button
                                key="disconnect_button"
                                type="button"
                                className={`transition-all rounded-lg h-10 px-6 bg-primary${is_loading ? '/20' : ''
                                    } text-white`}
                                disabled={is_loading}
                                onClick={async () => {
                                    delete attributes.to_disconnect
                                    isLoading(true)
                                    try {
                                        const res = await disconnectStripe(
                                            attributes.connected_account_id as unknown as string
                                        )

                                        if (res?.ok) {
                                            delete attributes.to_disconnect
                                            delete attributes.connected_account_id
                                            setAttributes({
                                                ...attributes,
                                            })
                                            getIntegrations()
                                        }
                                    } catch (e) {
                                        console.error(e)
                                    } finally {
                                        isLoading(false)
                                    }
                                }}
                            >
                                <Translation content_key="disconnect" />
                            </button>,
                        ]}
                    >
                        <Translation
                            content_key="delete_location_integration_note"
                            render_as="p"
                        />
                    </AlertPrompt>

                    <AlertPrompt
                        title="successfully_connected_to"
                        show={!!activated_integration?.name}
                        className="absolute top-0 flex flex-col items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm w-full h-full z-20"
                        handlebars={
                            activated_integration
                                ? {
                                    integration:
                                        activated_integration.name,
                                }
                                : {}
                        }
                        icon={
                            activated_integration?.logo ? (
                                <div
                                    className="rounded-full mx-auto h-16 w-16 bg-contain relative"
                                    style={{
                                        backgroundImage: `url(${activated_integration?.logo})`,
                                    }}
                                >
                                    <i className="feather-check bg-green-200 text-green-700 rounded-full py-1 px-1 block absolute top-0 right-0" />
                                </div>
                            ) : (
                                <div className="rounded-full mx-auto h-16 w-16 bg-slate-600 relative">
                                    <i className="feather-check bg-green-200 text-green-700 rounded-full py-1 px-1 block absolute top-0 right-0" />
                                </div>
                            )
                        }
                        actions={[
                            <button
                                key="back_button"
                                type="button"
                                className="rounded-lg h-10 px-6 bg-primary text-white"
                                onClick={() => {
                                    setAttributes({
                                        ...attributes,
                                        activated_integration: {},
                                    })
                                }}
                            >
                                <Translation content_key="back_to_locations" />
                            </button>,
                        ]}
                    />
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default LocationModal
